import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CalendarDays, Loader2, Trash2, Plus } from "lucide-react";

// Working calendar: Monday-Saturday (Sunday off), 2 PM pickup cut-off, default promise in working days, holidays.
const STATE_OPTS = ["Andhra Pradesh", "Telangana", "Karnataka", "Tamil Nadu"];
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export default function WorkingCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({ cutoff: "14:00", lane_days: 2 });
  const [hol, setHol] = useState({ date: "", name: "", states: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/v2/calendar"); setData(r.data); setCfg({ cutoff: r.data.config.cutoff, lane_days: r.data.config.lane_days }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load the calendar"); setData({ holidays: [], pickup_options: [], config: {} }); }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    setBusy(true);
    try { await api.put("/v2/calendar/config", { cutoff: cfg.cutoff, lane_days: Number(cfg.lane_days) }); toast.success("Saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const addHol = async () => {
    if (!hol.date || hol.name.trim().length < 2) { toast.error("Pick a date and give the holiday a name"); return; }
    setBusy(true);
    try { await api.post("/v2/calendar/holidays", { ...hol, name: hol.name.trim() }); toast.success("Holiday added"); setHol({ date: "", name: "", states: [] }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const delHol = async (d) => {
    if (!window.confirm(`Remove the holiday on ${fmt(d)}?`)) return;
    try { await api.delete(`/v2/calendar/holidays/${d}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!data) return <DashboardLayout><div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></DashboardLayout>;
  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-5" data-testid="working-calendar">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarDays className="w-6 h-6 text-brand-500" />Working calendar</h1>
          <p className="text-sm text-slate-500">Pickups Monday–Saturday. Sunday is always off. Holidays are not counted for pickup or for the promised delivery date.</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Rules</h2>
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <label className="text-xs font-semibold text-slate-600">Pickup cut-off (IST)
              <input type="time" value={cfg.cutoff} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, cutoff: e.target.value })} className="mt-1.5 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm" data-testid="cal-cutoff" />
            </label>
            <label className="text-xs font-semibold text-slate-600">Default promise (working days)
              <input type="number" min="1" max="10" value={cfg.lane_days} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, lane_days: e.target.value })} className="mt-1.5 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm" data-testid="cal-lane-days" />
            </label>
            {isAdmin && <button onClick={saveCfg} disabled={busy} className="h-10 px-4 rounded-lg bg-brand-500 text-white text-sm font-semibold disabled:opacity-50">Save rules</button>}
          </div>
          <p className="text-xs text-slate-500 mt-3">Booked before the cut-off on a working day → picked up the same day. After it, or on a Sunday / holiday → the next working day. NDD shipments promise 1 working day; enterprise lanes can set their own.</p>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {(data.pickup_options || []).map((o) => (
              <span key={o.date} className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 ${o.available ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-400"}`} title={o.reason || "Pickups on"}>
                {new Date(o.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric" })}{o.available ? "" : ` · ${o.reason}`}</span>))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Holidays</h2>
          {isAdmin && (
            <div className="grid sm:grid-cols-[160px_1fr_auto] gap-3 items-end mb-3">
              <label className="text-xs font-semibold text-slate-600">Date<input type="date" value={hol.date} onChange={(e) => setHol({ ...hol, date: e.target.value })} className="mt-1.5 w-full h-10 border border-slate-300 rounded-lg px-2 text-sm" data-testid="hol-date" /></label>
              <label className="text-xs font-semibold text-slate-600">Name<input value={hol.name} onChange={(e) => setHol({ ...hol, name: e.target.value })} placeholder="e.g. Sankranti" className="mt-1.5 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm" data-testid="hol-name" /></label>
              <button onClick={addHol} disabled={busy} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50"><Plus className="w-4 h-4" />Add</button>
              <div className="sm:col-span-3 flex flex-wrap gap-1.5 items-center text-xs text-slate-600"><span className="font-semibold">Only in:</span>
                {STATE_OPTS.map((st) => { const on = hol.states.includes(st); return (
                  <button key={st} type="button" onClick={() => setHol({ ...hol, states: on ? hol.states.filter((x) => x !== st) : [...hol.states, st] })}
                    className={`h-7 px-2.5 rounded-full border font-semibold ${on ? "bg-brand-500 border-brand-500 text-white" : "border-slate-300 bg-white"}`}>{st}</button>); })}
                <span className="text-slate-400">(none selected = everywhere)</span></div>
            </div>)}
          {data.holidays.length === 0 ? <p className="text-sm text-slate-400">No holidays added for this year.</p> : (
            <ul className="divide-y divide-slate-100">{data.holidays.map((h) => (
              <li key={h.date + h.name} className="py-2.5 flex items-center gap-3 text-sm">
                <span className="font-semibold w-44">{fmt(h.date)}</span><span className="flex-1">{h.name}</span>
                <span className="text-xs text-slate-500">{h.states?.length ? h.states.join(", ") : "Everywhere"}</span>
                {isAdmin && <button onClick={() => delHol(h.date)} aria-label={`Remove ${h.name}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
              </li>))}</ul>)}
        </div>
      </div>
    </DashboardLayout>
  );
}
